import { useState, useEffect, useRef } from "react";

export default function AddressAutocomplete({ value, onChange, onSelect }) {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.length < 3) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&q=${encodeURIComponent(query)}`,
          { headers: { "Accept-Language": "en" } }
        );
        const data = await res.json();
        setSuggestions(data);
        setShowDropdown(data.length > 0);
      } catch {
        setSuggestions([]);
      }
      setLoading(false);
    }, 400);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (onChange) onChange(val);
  };

  const handleSelect = (place) => {
    setQuery(place.display_name);
    setShowDropdown(false);
    setSuggestions([]);
    if (onSelect) {
      onSelect({
        address: place.display_name,
        lat: parseFloat(place.lat),
        lng: parseFloat(place.lon)
      });
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <input
        className="w-full p-2.5 bg-gray-50/80 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent"
        placeholder="Start typing a pickup address..."
        value={query}
        onChange={handleInputChange}
        onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
      />
      {loading && (
        <div className="absolute right-3 top-2.5 text-xs text-gray-400">Searching...</div>
      )}
      {showDropdown && (
        <ul className="absolute z-[1100] w-full bg-white/95 backdrop-blur-md border border-gray-200 rounded-xl mt-1 shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((place, i) => (
            <li
              key={place.place_id || i}
              onClick={() => handleSelect(place)}
              className="px-3 py-2 text-sm text-gray-700 hover:bg-green-50 cursor-pointer border-b border-gray-100 last:border-0"
            >
              <span className="font-medium">{place.display_name.split(",")[0]}</span>
              <span className="text-gray-400 text-xs block">{place.display_name.split(",").slice(1).join(",").trim()}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
